/**
 * Document Types Configuration
 *
 * Defines the document types and sections for the document upload form.
 * Used by the DocumentUploadForm component to render appropriate dropzones.
 */

export interface DocumentTypeConfig {
  key: string;
  label: string;
  maxSize: number; // MB
  accept?: string; // File extensions (e.g., '.xlsx,.xls')
  required?: boolean; // For validation hints
}

export interface DocumentSection {
  title: string;
  description?: string;
  types: DocumentTypeConfig[];
}

export const DOCUMENT_SECTIONS: Record<string, DocumentSection> = {
  income_expense: {
    title: 'Income and Expense Statements (IN EXCEL)',
    description:
      'Please upload your income and expense statements in Excel format (.xlsx or .xls)',
    types: [
      {
        key: 'income_expense_2022',
        label: '2022 Income and Expense Statement',
        maxSize: 100,
        accept: '.xlsx,.xls',
      },
      {
        key: 'income_expense_2023',
        label: '2023 Income and Expense Statement',
        maxSize: 100,
        accept: '.xlsx,.xls',
      },
      {
        key: 'income_expense_2024',
        label: '2024 Income and Expense Statement',
        maxSize: 100,
        accept: '.xlsx,.xls',
      },
      {
        key: 'income_expense_2025',
        label: '2025 Income and Expense Statement',
        maxSize: 100,
        accept: '.xlsx,.xls',
      },
    ],
  },
  str_reports: {
    title: 'Monthly STR Reports (IN EXCEL)',
    description:
      'Please upload your STR reports in Excel format (.xlsx or .xls)',
    types: [
      {
        key: 'str_dec_2020',
        label: 'December 2020 Monthly STR Report',
        maxSize: 100,
        accept: '.xlsx,.xls',
      },
      {
        key: 'str_dec_2024',
        label: 'December 2024 Monthly STR Report',
        maxSize: 100,
        accept: '.xlsx,.xls',
      },
      {
        key: 'str_most_recent',
        label: 'Most Recent Monthly STR Report',
        maxSize: 100,
        accept: '.xlsx,.xls',
      },
    ],
  },
  additional: {
    title: 'Additional Files',
    description:
      'Upload any additional supporting documents. All file types are accepted.',
    types: [
      {
        key: 'purchase_agreements',
        label: 'Purchase and Sale Agreements Over the Past 3 Years',
        maxSize: 250,
      },
      {
        key: 'site_survey',
        label: 'Site Survey',
        maxSize: 250,
      },
      {
        key: 'building_plans',
        label: 'Building Plans',
        maxSize: 250,
      },
      {
        key: 'prior_appraisals',
        label: 'Prior Appraisal Reports',
        maxSize: 250,
      },
      {
        key: 'capital_expenditures',
        label: 'Historical and Budgeted Capital Expenditures (PIP)',
        maxSize: 250,
      },
      {
        key: 'management_contract',
        label: 'Management Contract',
        maxSize: 250,
      },
      {
        key: 'franchise_agreement',
        label: 'Franchise Agreement',
        maxSize: 250,
      },
      {
        key: 'tax_bills',
        label: 'Tax Bills',
        maxSize: 250,
      },
      {
        key: 'title_deed',
        label: 'Title Report, Legal Description, and/or Deed',
        maxSize: 250,
      },
      {
        key: 'ground_lease',
        label: 'Ground Lease',
        maxSize: 250,
      },
      {
        key: 'construction_costs',
        label: 'Construction Costs',
        maxSize: 250,
      },
      {
        key: 'environmental',
        label: 'Environmental Report',
        maxSize: 250,
      },
    ],
  },
  retrospective_only: {
    title: 'Retrospective Documents',
    description:
      'Additional documents required for retrospective appraisals.',
    types: [
      {
        key: 'custom_trend',
        label: 'Custom Trend Report',
        maxSize: 250,
      },
      {
        key: 'additional_requested',
        label: 'Any Additional Files That Were Requested',
        maxSize: 250,
      },
    ],
  },
};

/**
 * Get document sections based on form type.
 *
 * @param formType - 'standard' or 'retrospective'
 * @returns Array of DocumentSection objects for the specified form type
 */
export function getDocumentSections(
  formType: 'standard' | 'retrospective'
): DocumentSection[] {
  const sections = [
    DOCUMENT_SECTIONS.income_expense,
    DOCUMENT_SECTIONS.str_reports,
    DOCUMENT_SECTIONS.additional,
  ];

  if (formType === 'retrospective') {
    sections.push(DOCUMENT_SECTIONS.retrospective_only);
  }

  return sections;
}
